import React, {useState, useRef, memo} from "react";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";

//components:
import {EnhancedTableHead, PaginationTable} from "../";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import CircularProgress from "@material-ui/core/CircularProgress";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";

export const ReusableTable = memo(
    ({headRows, allRows, filteredRows, formatRow, isLoading, emptyMessage, errorMessage, pagination}) => {
        const {t} = useTranslation();

        //local state:
        const [order, setOrder] = useState("asc");
        const [orderBy, setOrderBy] = useState("title");
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(5);

        const tableRef = useRef(null);

        const onWheel = e => {
            e.preventDefault();
            if (window.innerWidth < 1750) {
                const container = tableRef.current;
                const containerScrollPosition = tableRef.current.scrollLeft;
                container.scrollTo({
                    top: 0,
                    left: containerScrollPosition + e.deltaY,
                });
            }
        };

        const desc = (a, b, orderBy) => {
            const aLowerCase = (a[orderBy] || "").toString().toLowerCase();
            const bLowerCase = (b[orderBy] || "").toString().toLowerCase();

            if (bLowerCase < aLowerCase) {
                return -1;
            }
            if (bLowerCase > aLowerCase) {
                return 1;
            }
            return 0;
        };

        const stableSort = (array, cmp) => {
            const stabilizedThis = array.map((el, index) => [el, index]);
            stabilizedThis.sort((a, b) => {
                const order = cmp(a[0], b[0]);
                if (order !== 0) return order;
                return a[1] - b[1];
            });
            const stableSorted = stabilizedThis.map(el => el[0]);

            return stableSorted;
        };

        const getSorting = (order, orderBy) => {
            return order === "desc" ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
        };

        const handleRequestSort = (event, property) => {
            const isDesc = orderBy === property && order === "desc";
            setOrder(isDesc ? "asc" : "desc");
            setOrderBy(property);
        };

        const ErrorMessage = () => {
            const hasErrorText = errorMessage?.text;
            if (hasErrorText) {
                const alertType = errorMessage.type;
                const hasErrorLink = errorMessage.link?.path;
                const linkText = errorMessage.link?.text;

                return (
                    <div className={`alert alert-${alertType || "light"} w-fit-content d-flex align-items-center mt-4`}>
                        {hasErrorText}
                        {hasErrorLink ? <Link to={hasErrorLink}>{linkText}</Link> : ""}
                    </div>
                );
            }
            return "";
        };

        const Content = () => {
            if (!isLoading) {
                const hasRows = filteredRows?.length > 0;
                if (hasRows) {
                    return (
                        <div
                            className="custom-scroll-area horizontal-scroll"
                            style={{overflowY: "visible"}}
                            onWheel={onWheel}
                            ref={tableRef}
                        >
                            <Table>
                                {headRows ? (
                                    <EnhancedTableHead
                                        order={order}
                                        orderBy={orderBy}
                                        onRequestSort={handleRequestSort}
                                        rowCount={filteredRows?.length}
                                        headRows={headRows}
                                    />
                                ) : null}
                                <TableBody>
                                    {filteredRows &&
                                        stableSort(filteredRows, getSorting(order, orderBy))
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((row, i) => formatRow(row, i))}
                                </TableBody>
                                {/* pagination: */}
                                {pagination ? (
                                    <TableFooter>
                                        <TableRow>
                                            <PaginationTable
                                                setPage={setPage}
                                                setRowsPerPage={setRowsPerPage}
                                                rows={filteredRows}
                                                rowsPerPage={rowsPerPage}
                                                page={page}
                                                rowsPerPageOptions={[5, 10, 25]}
                                                variant="filled"
                                            />
                                        </TableRow>
                                    </TableFooter>
                                ) : null}
                            </Table>
                        </div>
                    );
                } else if (allRows && allRows.length)
                    return (
                        <div className="alert alert-light text-dark w-fit-content">
                            {t("TABLE.SEARCHBAR.NOTFOUND.MESSAGE")}
                        </div>
                    );
                else return <div className="alert alert-light text-dark w-fit-content mt-4">{emptyMessage}</div>;
            } else
                return (
                    <div className="d-flex justify-content-center align-items-center position-absolute top-0 left-0 w-100 h-100 bg-transparent">
                        <CircularProgress />
                    </div>
                );
        };

        return (
            <div className="reusable_table_container">
                <ErrorMessage />
                <Content />
            </div>
        );
    },
    (prevState, nextState) =>
        prevState.headRows === nextState.headRows &&
        prevState.allRows === nextState.allRows &&
        prevState.filteredRows === nextState.filteredRows &&
        prevState.formatRow === nextState.formatRow &&
        prevState.isLoading === nextState.isLoading &&
        prevState.emptyMessage === nextState.emptyMessage &&
        prevState.errorMessage === nextState.errorMessage &&
        prevState.pagination === nextState.pagination
);
